import { useState } from 'react'
import { RootState } from 'store/'
import { useAppDispatch } from 'store/hooks'
import { useSelector } from 'react-redux'
import { actions as dashboardActions } from 'store/dashboard'
import {
  Stack,
  Typography,
  Button,
  Collapse,
  Box,
  Menu,
  MenuItem,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import OpenCloseButton from 'components/openCloseButton/OpenCloseButton'
import RoundIconButton from 'components/roundIconButton/RoundIconButton'
import SectionContainer from 'containers/dashboard/components/SectionContainer'
import SectionHeader from 'containers/dashboard/components/SectionHeader'
import RegenerateReportSection from 'containers/dashboard/singleReportPage/components/RegenerateReportSection'
import DownloadIcon from '@mui/icons-material/Download'

import { SearchInfo } from 'store/dashboard/types'
import Spinner from 'components/spinner/Spinner'
import SectionSubTitle from 'containers/dashboard/components/SectionSubtitle'

type Props = {
  searchId: string
  searchInfo?: SearchInfo
}
export default function MainTitleSection({ searchId, searchInfo }: Props) {
  const dispatch = useAppDispatch()
  const roles = useSelector((state: RootState) => state.account.roles)
  const downloadPdfLoading = useSelector((state: RootState) => state.dashboard.loading.downloadPdf)
  const errorDashboard = useSelector((state: RootState) => state.dashboard.error)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [sectionOpened, setSectionOpened] = useState(false)

  const open = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }
  const downloadPdf = () => {
    if (!searchInfo) return
    const data = {
      searchId: searchInfo.search_id,
      pdfFileName: searchInfo!.name + '_' + searchInfo.created_at,
    }
    dispatch(dashboardActions.downloadPdf(data))
  }

  return (
    <SectionContainer>
      <SectionHeader>
        <Stack direction="row" alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h5"
            sx={{
              flex: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
            Single Patent Similarity Report -{' '}
            <Typography variant="h5" component="span" color="primary">
              {searchInfo?.name || ' '}
            </Typography>
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center">
          <Button
            id="action-button"
            aria-controls={open ? 'basic-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClick}
            sx={{ mr: 4 }}>
            Actions
          </Button>
          <Menu id="basic-menu" anchorEl={anchorEl} open={open} onClose={handleClose}>
            {roles?.includes('bo_admin') && (
              <>
                <MenuItem onClick={() => {}}>
                  <RegenerateReportSection searchId={searchId} />
                </MenuItem>
                <MenuItem onClick={downloadPdf} disabled={downloadPdfLoading}>
                  <RoundIconButton>
                    <DownloadIcon sx={{ cursor: 'pointer', width: 17 }} />
                  </RoundIconButton>
                  Get PDF{downloadPdfLoading && <Spinner />}
                  {errorDashboard.downloadPdf && (
                    <Box ml={1} sx={{ color: 'red' }}>
                      {errorDashboard.downloadPdf}
                    </Box>
                  )}
                </MenuItem>
              </>
            )}
          </Menu>
          <OpenCloseButton onClick={() => setSectionOpened(!sectionOpened)} opened={sectionOpened} />
        </Stack>
      </SectionHeader>
      <Collapse in={sectionOpened}>
        {searchInfo && (
          <>
            <Typography sx={{ ml: 3, mb: 1 }} variant="body2" color={'text.secondary'} mr={4}>
              {searchInfo.created_at}
            </Typography>
            <Paper variant="outlined" sx={{ borderRadius: '20px', mx: 3, mb: 2 }}>
              <SectionSubTitle title="Search dimensions" />
              <Divider />

              <TableContainer sx={{ maxHeight: 700 }}>
                <Table stickyHeader aria-label="sticky table">
                  <TableHead>
                    <TableRow>
                      <TableCell key={1}>Report Name</TableCell>
                      <TableCell key={2}>Created by</TableCell>
                      <TableCell key={3}>Year cutoff</TableCell>
                      <TableCell key={4}>Search ID</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell key={1}>{searchInfo.name}</TableCell>
                      <TableCell key={2}>{searchInfo.firstname + ' ' + searchInfo.lastname}</TableCell>
                      <TableCell key={3}>{searchInfo.year_cutoff}</TableCell>
                      <TableCell key={4}>{searchInfo.search_id}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <Stack direction="row" justifyContent="space-between"></Stack>
            </Paper>
            <Paper variant="outlined" sx={{ borderRadius: '20px', mx: 3, mb: 2 }}>
              <SectionSubTitle title="Sectors" />
              <Divider />
              <Typography fontSize={12} sx={{ p: 2 }}>
                {searchInfo.filter_classifications_ipcr.join(', ')}
              </Typography>
            </Paper>
            <Paper variant="outlined" sx={{ borderRadius: '20px', mx: 3, mb: 3 }}>
              <SectionSubTitle title="Territories" />
              <Divider />
              <Typography fontSize={12} sx={{ p: 2 }}>
                {searchInfo.countries.join(', ')}
              </Typography>
            </Paper>
          </>
        )}
      </Collapse>
    </SectionContainer>
  )
}
